import { useEffect, useState } from 'react';
import { localize, type Service } from '@360face/domain';
import { ApiError } from '../api/client';
import type { HotWindowsPreview, StaffApi } from './api';
import { contentLocale, type TabletLocale, type TabletMessages } from './messages';

/** The treatment offered first: the 60-minute facial lifting, else the first face treatment. */
export function defaultHotService(catalog: Service[]): Service | undefined {
  const main = catalog.filter((s) => s.active && s.kind !== 'addon');
  return (
    main.find((s) => s.durationMin === 60 && /signature|lifting/i.test(s.title.en ?? s.title.pt)) ??
    main.find((s) => s.kind === 'face') ??
    main[0]
  );
}

/**
 * «Hot windows» (owner 23.10 / 04.11.2026): today's free times of the studio for one treatment; the
 * therapist picks up to four and sends one notification to the app users who asked for it.
 * Sending asks the PIN again. Opened from the day screen (button «🔥») or /tablet#hot.
 */
export function HotWindows({
  api,
  catalog,
  t,
  locale,
  studio,
  onBack,
}: {
  api: StaffApi;
  catalog: Service[];
  /** Short studio name as in the notification title (e.g. «Parede»). */
  studio: string;
  t: TabletMessages;
  locale: TabletLocale;
  onBack: () => void;
}) {
  const services = catalog.filter((s) => s.active && s.kind !== 'addon');
  const [serviceId, setServiceId] = useState(() => defaultHotService(catalog)?.id ?? '');
  const [data, setData] = useState<HotWindowsPreview>();
  const [picked, setPicked] = useState<string[]>([]);
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [sent, setSent] = useState<number>();

  useEffect(() => {
    if (!serviceId && catalog.length) setServiceId(defaultHotService(catalog)?.id ?? '');
  }, [catalog, serviceId]);
  useEffect(() => {
    let live = true;
    setData(undefined);
    setError(undefined);
    api.hotWindows(serviceId || undefined).then(
      (d) => {
        if (!live) return;
        setData(d);
        setPicked(d.times.slice(0, 4).map((x) => x.time));
      },
      (e) => live && setError(err(e)),
    );
    return () => {
      live = false;
    };
  }, [api, serviceId, t]);

  const err = (e: unknown) => t.errors[e instanceof ApiError ? e.code : 'default'] ?? t.errors.default;
  const toggle = (time: string) =>
    setPicked((xs) => (xs.includes(time) ? xs.filter((x) => x !== time) : xs.length >= 4 ? xs : [...xs, time].sort()));
  const and = { pt: ' e ', en: ' and ', ru: ' и ' }[locale];
  const list = picked.length > 1 ? `${picked.slice(0, -1).join(', ')}${and}${picked.at(-1)}` : (picked[0] ?? '…');
  const head = { pt: 'Horários livres hoje', en: 'Free times today', ru: 'Свободное время сегодня' }[locale];
  const preview = {
    pt: `Hoje às ${list}. Toque para marcar.`,
    en: `Today at ${list}. Tap to book.`,
    ru: `Сегодня в ${list}. Нажмите, чтобы записаться.`,
  }[locale];

  const send = async () => {
    setBusy(true);
    setError(undefined);
    try {
      const r = await api.sendHotWindows(picked, pin);
      setSent(r.sent);
      setPin('');
      setData(await api.hotWindows(serviceId || undefined));
    } catch (e) {
      setError(err(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="tablet t3">
      <header className="t-bar">
        <button className="t-ghost" onClick={onBack}>
          {t.back}
        </button>
        <strong>{t.hotTitle}</strong>
        <span />
      </header>
      <div className="t-hot">
        <label className="t-hot-row">
          <span className="t-proto-label">{t.hotService}</span>
          <select value={serviceId} onChange={(e) => setServiceId(e.target.value)} aria-label={t.hotService}>
            {services.map((s) => (
              <option key={s.id} value={s.id}>
                {localize(s.title, contentLocale(locale))} · {s.durationMin} min
              </option>
            ))}
          </select>
        </label>
        {!data && !error && <p className="t-note">…</p>}
        {data && (
          <>
            <span className="t-proto-label">{t.hotTimes}</span>
            {data.times.length === 0 ? (
              <p className="t-note">{t.hotNone}</p>
            ) : (
              <div className="t-hot-times">
                {data.times.map((x) => {
                  const on = picked.includes(x.time);
                  return (
                    <button
                      key={x.time}
                      type="button"
                      className={on ? 't-hot-time on' : 't-hot-time'}
                      aria-pressed={on}
                      onClick={() => toggle(x.time)}
                    >
                      <b>{x.time}</b>
                      <small>{x.staff.join(', ')}</small>
                    </button>
                  );
                })}
              </div>
            )}
            <section className="t-hot-preview" aria-label={t.hotPreview}>
              <small>{t.hotPreview}</small>
              <b>
                🔥 {head} · {studio}
              </b>
              <span>{preview}</span>
            </section>
            <p className="t-hot-aud">{t.hotAudience(data.audience.waiting, data.audience.total)}</p>
            <p className="t-note">{t.hotRule}</p>
            {!data.enabled && <p className="t-warn">{t.hotOff}</p>}
            <div className="t-hot-send">
              <input
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={8}
                placeholder={t.pin}
                aria-label={t.pin}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              />
              <button
                type="button"
                className="t-primary"
                disabled={busy || !data.enabled || picked.length === 0 || pin.length < 4 || data.audience.waiting === 0}
                onClick={() => void send()}
              >
                {t.hotSend}
              </button>
            </div>
          </>
        )}
        {sent !== undefined && <p className="t-info">{t.hotSent(sent)}</p>}
        {error && <p className="t-error">{error}</p>}
      </div>
    </div>
  );
}
